import { useMemo, useState, type FormEvent } from "react";
import { DICT, type DictKey, type Lang } from "./_home/dict";
import { image, text } from "./_home/content";
import { formatPrice, type Offer, type SiteSnapshot } from "./lib/products";
import { submitLead } from "./lib/api";
import { homeHref, tripHref, isPlainClick } from "./lib/nav";

/** Persona landing pages (/personas/<slug>/). One template, four personas;
 *  copy comes from the editable `landing_<persona>` site records, the trip grid
 *  is every offer tagged with this persona, and the CTA is an inquiry form
 *  tagged so segmented traffic is trackable in HighLevel. */

export const PERSONA_SLUGS = ["women", "men", "mixed", "private"] as const;
export type Persona = (typeof PERSONA_SLUGS)[number];

export function isPersona(s: string): s is Persona {
  return (PERSONA_SLUGS as readonly string[]).includes(s);
}

// Same default hero image as the homepage — editable per page via the
// landing record's `hero_image`.
const HERO_DEFAULT_IMG =
  "https://images.unsplash.com/photo-1509840841025-9088ba78a826?auto=format&fit=crop&w=2400&q=80";

// Fallback copy if the site snapshot is unavailable (e.g. dev server with no
// build snapshot). Production reads the real copy from the landing records.
const FALLBACK: Record<Persona, { eyebrow: Record<Lang, string>; intro: Record<Lang, string> }> = {
  women: {
    eyebrow: { en: "Explore Girls' Trips", es: "Explora Viajes de Chicas" },
    intro: {
      en: "Your girls' trip, done right. Stunning destinations, zero stress, and the kind of travel that reminds you why you needed this.",
      es: "Tu viaje de chicas, bien hecho. Destinos impresionantes, cero estrés y el tipo de viaje que te recuerda por qué lo necesitabas.",
    },
  },
  men: {
    eyebrow: { en: "Explore Bro-cations", es: "Explora Bro-cations" },
    intro: {
      en: "Elevated, adventure-ready trips for guys who want to travel well without anyone having to figure out the details.",
      es: "Viajes elevados y listos para la aventura para hombres que quieren viajar bien sin ocuparse de los detalles.",
    },
  },
  mixed: {
    eyebrow: { en: "Explore Mixed Group Tours", es: "Explora Viajes en Grupo Mixto" },
    intro: {
      en: "The more the merrier — when it's planned right. Dynamic, hassle-free group travel for mixed crews.",
      es: "Cuantos más, mejor — cuando está bien planificado. Viajes en grupo dinámicos y sin complicaciones para grupos mixtos.",
    },
  },
  private: {
    eyebrow: { en: "Explore Private Trips", es: "Explora Viajes Privados" },
    intro: {
      en: "Your trip, your rules. Fully curated, private travel experiences built entirely around you.",
      es: "Tu viaje, tus reglas. Experiencias de viaje privadas y totalmente personalizadas, construidas en torno a ti.",
    },
  },
};

interface PersonaPageProps {
  persona: Persona;
  lang: Lang;
  site?: SiteSnapshot;
  offers: Offer[] | null;
  offersError: boolean;
  onHome: () => void;
  onTrip: (slug: string) => void;
}

interface SubState {
  busy: boolean;
  msg: { kind: "ok" | "err"; text: string } | null;
}

export default function PersonaPage({
  persona,
  lang,
  site,
  offers,
  offersError,
  onHome,
  onTrip,
}: PersonaPageProps) {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [sub, setSub] = useState<SubState>({ busy: false, msg: null });

  const t = (key: DictKey) => DICT[lang][key];
  const recordSlug = `landing_${persona}`;
  const fb = FALLBACK[persona];

  const eyebrow = text(site, lang, recordSlug, "eyebrow", fb.eyebrow[lang]);
  const intro = text(site, lang, recordSlug, "intro", fb.intro[lang]);
  const ctaLabel = text(site, lang, recordSlug, "cta", lang === "es" ? "Planifica tu viaje" : "Plan your trip");
  const heroImg = image(site, recordSlug, "hero_image", HERO_DEFAULT_IMG);

  const trips = useMemo(
    () => (offers ?? []).filter((o) => o.personas?.includes(persona)),
    [offers, persona],
  );

  const onInquire = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email || sub.busy) return;
    setSub({ busy: true, msg: null });
    try {
      const res = await submitLead({
        email,
        source: `persona:${persona}`,
        locale: lang,
        note: note || undefined,
        tags: ["persona-inquiry", `persona:${persona}`],
      });
      if (res.ok) {
        setSub({ busy: false, msg: { kind: "ok", text: t("trip_form_ok") } });
        setEmail("");
        setNote("");
      } else {
        setSub({ busy: false, msg: { kind: "err", text: t("foot_sub_err") } });
      }
    } catch {
      setSub({ busy: false, msg: { kind: "err", text: t("foot_sub_err") } });
    }
  };

  return (
    <main className="lp">
      <header className="lp-topbar">
        <a
          className="lp-home"
          href={homeHref(lang)}
          onClick={(e) => {
            if (!isPlainClick(e)) return;
            e.preventDefault();
            onHome();
          }}
        >
          ← Bougie Travel Club
        </a>
      </header>

      <section
        className="lp-hero"
        style={{ backgroundImage: `linear-gradient(rgba(22,38,61,0.55), rgba(22,38,61,0.55)), url('${heroImg}')` }}
      >
        <div className="lp-hero-inner">
          <div className="lp-eyebrow">{eyebrow}</div>
          <p className="lp-intro">{intro}</p>
          <a className="btn-primary lp-cta" href="#lp-inquire">
            <span>{ctaLabel}</span>
          </a>
        </div>
      </section>

      <section className="lp-trips" id="lp-trips">
        {offersError ? (
          <p className="offers-state offers-state-err">{t("offers_empty")}</p>
        ) : offers === null ? (
          <p className="offers-state">{t("offers_loading")}</p>
        ) : trips.length === 0 ? (
          <p className="offers-state">
            {lang === "es"
              ? "Pronto — cuéntanos qué tienes en mente abajo."
              : "Coming soon — tell us what you have in mind below."}
          </p>
        ) : (
          <div className="offers-grid">
            {trips.map((o) => {
              const key = o.slug ?? o.id;
              return (
                <a
                  key={o.id}
                  className="offer-card"
                  href={tripHref(lang, key)}
                  onClick={(e) => {
                    if (!isPlainClick(e)) return;
                    e.preventDefault();
                    onTrip(key);
                  }}
                >
                  {o.image && (
                    <div
                      className="offer-img"
                      style={{ backgroundImage: `url('${o.image}')` }}
                      role="img"
                      aria-label={o.name}
                    />
                  )}
                  <div className="offer-body">
                    {o.price && <div className="offer-price">{formatPrice(o.price, lang)}</div>}
                    <h3 className="offer-name">{o.name}</h3>
                    <span className="offer-cta">
                      <span>{lang === "es" ? "Ver viaje" : "View trip"}</span> →
                    </span>
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </section>

      <section className="lp-inquire" id="lp-inquire">
        <h2 className="lp-inq-title">{eyebrow}</h2>
        <p className="lp-inq-deck">{t("trip_inq_deck")}</p>
        <form className="trip-inq-form" onSubmit={onInquire}>
          <input
            className="trip-inq-input"
            type="email"
            required
            placeholder={t("trip_inq_email_ph")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={sub.busy}
          />
          <textarea
            className="trip-inq-note"
            rows={3}
            placeholder={t("trip_inq_note_ph")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={sub.busy}
            maxLength={500}
          />
          <button className="btn-primary trip-inq-submit" type="submit" disabled={sub.busy}>
            {sub.busy ? "…" : ctaLabel}
          </button>
        </form>
        {sub.msg && (
          <p className={`trip-inq-msg${sub.msg.kind === "err" ? " is-err" : ""}`}>{sub.msg.text}</p>
        )}
      </section>
    </main>
  );
}
